import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Save, Plus, Trash2, Eye, EyeOff, Upload,
  GraduationCap, Briefcase, Award, ScrollText, User,
  ToggleLeft, ToggleRight, Camera, FileSearch,
  Paperclip, CheckCircle, XCircle, Clock, AlertTriangle, ExternalLink,
  Image as ImageIcon, X, Lock,
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';
import AdminSidebar from '../components/AdminSidebar';
import AddressSearch from '../components/AddressSearch';
import { getAllProvinces, getDistricts } from '../data/regions';
import RichTextEditor from '../components/RichTextEditor';
import ResumeTemplate1Preview from '../components/ResumeTemplate1Preview';
import ResumeTemplate2Preview from '../components/ResumeTemplate2Preview';
import ResumeTemplate3Preview from '../components/ResumeTemplate3Preview';
import ResumeModalScaler from '../components/ResumeModalScaler';
import ProfilePhotoCropModal from '../components/ProfilePhotoCropModal';

// --- 타입 정의 ---

interface EducationEntry {
  id: string;
  type: 'COLLEGE' | 'GRADUATE';
  year: string;
  schoolName: string;
  major: string;
  status: 'GRADUATED' | 'COMPLETED' | 'ENROLLED';
  // 대학원 학위 종류 (GRADUATE 전용)
  degreeLevel?: 'MASTERS' | 'DOCTORATE';
  // 학위증명서
  degreeCertificateUrl?: string | null;
  degreeCertificateExt?: string | null;
  degreeCertificateUploadedAt?: string | null;
  degreeReviewStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' | null;
  degreeReviewedAt?: string | null;
  degreeRejectionReason?: string | null;
}

interface ExperienceEntry {
  id: string;
  type: 'FULL_TIME' | 'PART_TIME' | 'FREELANCE' | 'INTERN' | 'OTHER';
  year: string;
  company: string;
  position: string;
  description: string;
}

interface AwardEntry {
  id: string;
  year: string;
  title: string;
}

interface CertificateEntry {
  id: string;
  name: string;
  issuer: string;
  date: string;
  // 자격증/수료증 증명 파일
  credentialUrl?: string | null;
  credentialExt?: string | null;
  credentialUploadedAt?: string | null;
  reviewStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' | null;
  reviewedAt?: string | null;
  rejectionReason?: string | null;
}

interface Visibility {
  education: boolean;
  experience: boolean;
  awards: boolean;
  certificates_list: boolean;
  introduction: boolean;
  stampfit_certificates: boolean;
  portfolio: boolean;
}

// --- 상수 ---

const EDUCATION_TYPES = [
  { value: 'COLLEGE', label: '대학교' },
  { value: 'GRADUATE', label: '대학원' },
];

const EDUCATION_STATUS = [
  { value: 'GRADUATED', label: '졸업' },
  { value: 'COMPLETED', label: '수료' },
  { value: 'ENROLLED', label: '재학' },
];

const DEGREE_LEVELS = [
  { value: 'MASTERS', label: '석사' },
  { value: 'DOCTORATE', label: '박사' },
];

const EXPERIENCE_TYPES = [
  { value: 'FULL_TIME', label: '정규 근무' },
  { value: 'PART_TIME', label: '파트타임' },
  { value: 'FREELANCE', label: '프리랜서' },
  { value: 'INTERN', label: '인턴' },
  { value: 'OTHER', label: '기타' },
];

const DESIRED_WORK_TYPES = [
  { value: 'FULL_TIME', label: '정규직' },
  { value: 'CONTRACT', label: '계약직' },
  { value: 'PART_TIME', label: '파트타임' },
  { value: 'FREELANCE', label: '프리랜서' },
  { value: 'ANY', label: '무관' },
];

const SALARY_TYPES = [
  { value: 'HOURLY', label: '시급' },
  { value: 'MONTHLY', label: '월급' },
  { value: 'YEARLY', label: '연봉' },
  { value: 'NEGOTIABLE', label: '협의' },
];

const makeId = () => Math.random().toString(36).slice(2, 10);

const emptyEducation = (type: string): EducationEntry => ({
  id: makeId(), type: type as EducationEntry['type'],
  year: '', schoolName: '', major: '', status: 'GRADUATED',
  ...(type === 'GRADUATE' ? { degreeLevel: 'MASTERS' as const } : {}),
});

const emptyExperience = (type: string): ExperienceEntry => ({
  id: makeId(), type: type as ExperienceEntry['type'],
  year: '', company: '', position: '', description: '',
});

const emptyAward = (): AwardEntry => ({ id: makeId(), year: '', title: '' });

const emptyCertificate = (): CertificateEntry => ({ id: makeId(), name: '', issuer: '', date: '' });

export default function ResumePage() {
  const { user, isAuthenticated, checkSession } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Resume data
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoImageToCrop, setPhotoImageToCrop] = useState<string | null>(null);
  const [isSavingPhoto, setIsSavingPhoto] = useState(false);
  const [isDeletingPhoto, setIsDeletingPhoto] = useState(false);
  const [education, setEducation] = useState<EducationEntry[]>([emptyEducation('COLLEGE')]);
  const [experience, setExperience] = useState<ExperienceEntry[]>([emptyExperience('FULL_TIME')]);
  const [awards, setAwards] = useState<AwardEntry[]>([emptyAward()]);
  const [certificatesList, setCertificatesList] = useState<CertificateEntry[]>([emptyCertificate()]);
  const [introduction, setIntroduction] = useState('');
  const [visibility, setVisibility] = useState<Visibility>({
    education: true, experience: true, awards: true,
    certificates_list: true, introduction: true, stampfit_certificates: true,
    portfolio: true,
  });
  const [portfolioImages, setPortfolioImages] = useState<string[]>(['', '', '']);
  // 포트폴리오 변경은 "이력서 저장" 클릭 전까지 클라 메모리에만 보관
  const [portfolioPending, setPortfolioPending] = useState<{ file?: File; dataUrl?: string; delete?: boolean }[]>([{}, {}, {}]);

  // Job seeking
  const [isJobSeeking, setIsJobSeeking] = useState(false);
  const [desiredPosition, setDesiredPosition] = useState('');
  const [desiredSalaryType, setDesiredSalaryType] = useState('MONTHLY');
  const [desiredSalaryMin, setDesiredSalaryMin] = useState('');
  const [desiredSalaryMax, setDesiredSalaryMax] = useState('');
  const [desiredLocationProvince, setDesiredLocationProvince] = useState('');
  const [desiredLocationDistrict, setDesiredLocationDistrict] = useState('');
  const [desiredWorkType, setDesiredWorkType] = useState('ANY');
  const [address, setAddress] = useState('');

  // StampFit certificates (auto-loaded)
  const [stampfitCerts, setStampfitCerts] = useState<any[]>([]);

  // Preview / save modal
  const [showPreview, setShowPreview] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [previewStyle, setPreviewStyle] = useState<1 | 2 | 3>(1);

  // 학위증명서 업로드 상태
  const [uploadingDegreeId, setUploadingDegreeId] = useState<string | null>(null);
  const [reuploadConfirm, setReuploadConfirm] = useState<{ eduId: string; file: File } | null>(null);

  // 자격증/수료증 증명 파일 업로드 상태
  const [uploadingCredentialId, setUploadingCredentialId] = useState<string | null>(null);
  const [credentialReuploadConfirm, setCredentialReuploadConfirm] = useState<{ certId: string; file: File } | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    checkSession();
  }, [isAuthenticated, navigate, checkSession]);

  // Load resume
  useEffect(() => {
    const fetchResume = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/v1/resume', { credentials: 'include' });
        if (response.ok) {
          const result = await response.json();
          if (result.data) {
            const r = result.data;
            if (r.photo_url) setPhotoUrl(r.photo_url);
            if (r.education?.length) setEducation(r.education);
            if (r.experience?.length) setExperience(r.experience);
            if (r.awards?.length) setAwards(r.awards);
            if (r.certificates_list?.length) setCertificatesList(r.certificates_list);
            if (r.introduction) setIntroduction(r.introduction);
            if (r.visibility) setVisibility({
              education: true, experience: true, awards: true,
              certificates_list: true, introduction: true, stampfit_certificates: true,
              portfolio: true,
              ...r.visibility,
            });
            if (Array.isArray(r.portfolio_images)) {
              const next = ['', '', ''];
              r.portfolio_images.slice(0, 3).forEach((url: string, i: number) => { next[i] = url || ''; });
              setPortfolioImages(next);
            }
            if (r.resume_template && [1, 2, 3].includes(r.resume_template)) {
              setPreviewStyle(r.resume_template as 1 | 2 | 3);
            }
            setIsJobSeeking(r.is_job_seeking || false);
            setDesiredPosition(r.desired_position || '');
            setDesiredSalaryType(r.desired_salary_type || 'MONTHLY');
            setDesiredSalaryMin(r.desired_salary_min?.toString() || '');
            setDesiredSalaryMax(r.desired_salary_max?.toString() || '');
            setDesiredLocationProvince(r.desired_location_province || '');
            setDesiredLocationDistrict(r.desired_location_district || '');
            setDesiredWorkType(r.desired_work_type || 'ANY');
            setAddress(r.address || '');
          }
        }

        // Load StampFit certificates
        const certResponse = await fetch('/api/v1/certificates', { credentials: 'include' });
        if (certResponse.ok) {
          const certResult = await certResponse.json();
          setStampfitCerts(certResult.certificates || []);
        }
      } catch (err) {
        console.error('Failed to fetch resume:', err);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchResume();
  }, [user]);

  // PDF 출력 — 새 창에서 resume HTML만 인쇄 (앱 HTML 간섭 없음)
  const handlePrint = () => {
    const root = document.getElementById('resume-print-root');
    if (!root) return;

    const html = root.innerHTML;
    const win = window.open('', '_blank');
    if (!win) return;

    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@100;200;400;600;700&display=swap" rel="stylesheet">
  <style>
    @page { size: ${previewStyle === 3 ? 'A4 landscape' : 'A4 portrait'}; margin: 0; }
    *, *::before, *::after {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
      box-sizing: border-box;
    }
    html, body {
      margin: 0;
      padding: 0;
      background: white;
      font-family: 'Noto Sans KR', sans-serif;
    }
    .resume-template-root {
      display: flex;
      flex-direction: column;
      gap: 0 !important;
    }
    .resume-page {
      width: ${previewStyle === 3 ? '297mm' : '210mm'};
      height: ${previewStyle === 3 ? '210mm' : '297mm'};
      overflow: hidden;
      page-break-after: always;
      break-after: page;
      page-break-inside: avoid;
      break-inside: avoid;
      position: relative;
      display: ${previewStyle === 3 ? 'flex' : 'block'};
      flex-direction: ${previewStyle === 3 ? 'row' : 'unset'};
      margin: 0 !important;
    }
  </style>
</head>
<body>${html}</body>
</html>`);
    win.document.close();

    // 이미지·폰트 로드 후 인쇄 (onload와 setTimeout 중 먼저 도착한 것만 실행)
    let printed = false;
    const doPrint = () => {
      if (printed) return;
      printed = true;
      win.focus();
      win.print();
    };
    win.onload = doPrint;
    setTimeout(doPrint, 1200);
  };

  if (!isAuthenticated) return null;

  // --- Handlers ---

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      // 1) 포트폴리오 pending 변경을 일괄 적용 (업로드/삭제)
      const finalPortfolio = await applyPortfolioChanges();

      // 2) 본문 필드 PUT
      const filteredEdu = education.filter(e => e.year && e.schoolName);
      const filteredExp = experience.filter(e => e.year && e.company);
      const filteredAwards = awards.filter(a => a.year && a.title);
      const filteredCerts = certificatesList.filter(c => c.name);

      const response = await fetch('/api/v1/resume', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          education: filteredEdu,
          experience: filteredExp,
          awards: filteredAwards,
          certificatesList: filteredCerts,
          introduction,
          visibility,
          desiredPosition,
          desiredSalaryType,
          desiredSalaryMin,
          desiredSalaryMax,
          desiredLocation: [desiredLocationProvince, desiredLocationDistrict].filter(Boolean).join(' '),
          desiredLocationProvince: desiredLocationProvince || null,
          desiredLocationDistrict: desiredLocationDistrict || null,
          desiredWorkType,
          address,
          portfolioImages: finalPortfolio.filter(u => u),
          resumeTemplate: previewStyle,
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      // 3) 클라 상태 동기화
      setPortfolioImages(finalPortfolio);
      setPortfolioPending([{}, {}, {}]);
      setShowSaveModal(false);
      setMessage({ type: 'success', text: '이력서가 저장되었습니다.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || '저장에 실패했습니다.' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleJobSeeking = async () => {
    try {
      const newValue = !isJobSeeking;
      const response = await fetch('/api/v1/resume/job-seeking', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isJobSeeking: newValue }),
      });
      if (response.ok) {
        setIsJobSeeking(newValue);
        setMessage({ type: 'success', text: newValue ? '구직 상태가 ON으로 변경되었습니다.' : '구직 상태가 OFF로 변경되었습니다.' });
      }
    } catch (err) {
      console.error('Failed to toggle job seeking:', err);
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: '이미지 크기는 5MB 이하여야 합니다.' });
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoImageToCrop(reader.result as string);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleClosePhotoCropModal = () => {
    if (isSavingPhoto) return;
    setPhotoImageToCrop(null);
  };

  const handleSaveCroppedPhoto = async (croppedImage: Blob) => {
    setIsSavingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', croppedImage, 'photo.jpg');
      const response = await fetch('/api/v1/resume/photo', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const result = await response.json();
      if (response.ok) {
        setPhotoUrl(result.data.url);
        setPhotoImageToCrop(null);
      } else {
        setMessage({ type: 'error', text: result.error || '사진 업로드에 실패했습니다.' });
      }
    } catch (err) {
      console.error('Photo upload failed:', err);
      setMessage({ type: 'error', text: '사진 업로드 중 오류가 발생했습니다.' });
    } finally {
      setIsSavingPhoto(false);
    }
  };

  const handleDeletePhoto = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!photoUrl || isDeletingPhoto) return;
    if (!window.confirm('프로필 사진을 삭제하시겠습니까?')) return;
    setIsDeletingPhoto(true);
    try {
      const response = await fetch('/api/v1/resume/photo', {
        method: 'DELETE',
        credentials: 'include',
      });
      const result = await response.json();
      if (response.ok) {
        setPhotoUrl(null);
      } else {
        setMessage({ type: 'error', text: result.error || '사진 삭제에 실패했습니다.' });
      }
    } catch (err) {
      console.error('Photo delete failed:', err);
      setMessage({ type: 'error', text: '사진 삭제 중 오류가 발생했습니다.' });
    } finally {
      setIsDeletingPhoto(false);
    }
  };

  const toggleVisibility = (key: keyof Visibility) => {
    setVisibility(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // 포트폴리오: 표시용 url + 변경 여부 도우미
  const getPortfolioDisplayUrl = (slot: number): string => {
    const p = portfolioPending[slot];
    if (p?.delete) return '';
    if (p?.dataUrl) return p.dataUrl;
    return portfolioImages[slot] || '';
  };
  const isPortfolioSlotDirty = (slot: number) => {
    const p = portfolioPending[slot];
    return !!(p?.file || p?.delete);
  };
  const hasPortfolioChanges = portfolioPending.some(p => p?.file || p?.delete);

  // 파일 선택 / 교체 — 클라 메모리에만 보관, 저장 버튼 누를 때 업로드
  const handlePortfolioPick = (slot: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: '이미지 파일만 업로드할 수 있습니다.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: '이미지 크기는 5MB 이하여야 합니다.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : '';
      setPortfolioPending(prev => {
        const next = [...prev];
        next[slot] = { file, dataUrl, delete: false };
        return next;
      });
    };
    reader.readAsDataURL(file);
  };

  // 슬롯 비우기 — pending 새 파일이 있으면 그걸 취소하고, 서버 url은 삭제 예약으로 표시
  const handlePortfolioRemove = (slot: number) => {
    setPortfolioPending(prev => {
      const next = [...prev];
      const p = next[slot];
      if (p?.file) {
        // 아직 저장 전이라 그냥 새 파일 선택을 취소
        next[slot] = {};
      } else if (portfolioImages[slot]) {
        // 서버에 있는 이미지 → 저장 시 삭제 예약
        next[slot] = { delete: true };
      } else {
        next[slot] = {};
      }
      return next;
    });
  };

  // 변경 되돌리기
  const handlePortfolioRevert = (slot: number) => {
    setPortfolioPending(prev => {
      const next = [...prev];
      next[slot] = {};
      return next;
    });
  };

  // 저장 시 호출 — pending을 일괄 적용하고 서버에서 받은 최신 배열 반환
  const applyPortfolioChanges = async (): Promise<string[]> => {
    let latest = [...portfolioImages];
    while (latest.length < 3) latest.push('');
    const merge = (arr: any) => {
      const a = Array.isArray(arr) ? arr : [];
      const next = ['', '', ''];
      a.slice(0, 3).forEach((u: string, i: number) => { next[i] = u || ''; });
      return next;
    };

    for (let slot = 0; slot < 3; slot++) {
      const p = portfolioPending[slot];
      if (p?.file) {
        const fd = new FormData();
        fd.append('file', p.file);
        fd.append('slot', String(slot));
        const r = await fetch('/api/v1/resume/portfolio-image', {
          method: 'POST',
          credentials: 'include',
          body: fd,
        });
        const result = await r.json();
        if (!r.ok) throw new Error(result.error || '포트폴리오 업로드에 실패했습니다.');
        latest = merge(result.data?.portfolioImages);
      } else if (p?.delete && portfolioImages[slot]) {
        const r = await fetch(`/api/v1/resume/portfolio-image/${slot}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        const result = await r.json();
        if (!r.ok) throw new Error(result.error || '포트폴리오 삭제에 실패했습니다.');
        latest = merge(result.data?.portfolioImages);
      }
    }
    return latest;
  };

  // 학위증명서 업로드 (재업로드 시 경고 후 진행)
  const handleDegreeFileChange = (eduId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: '이미지(JPG/PNG/WEBP/GIF/HEIC) 또는 PDF만 업로드할 수 있습니다.' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: '파일 크기는 10MB 이하여야 합니다.' });
      return;
    }

    const item = education.find(e2 => e2.id === eduId);
    // 재업로드(이미 url이 있는 경우)면 경고 모달
    if (item?.degreeCertificateUrl) {
      setReuploadConfirm({ eduId, file });
    } else {
      doUploadDegreeCertificate(eduId, file);
    }
  };

  const doUploadDegreeCertificate = async (eduId: string, file: File) => {
    setUploadingDegreeId(eduId);
    setReuploadConfirm(null);
    setMessage(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('eduId', eduId);
      const response = await fetch('/api/v1/resume/degree-certificate', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const result = await response.json();
      if (!response.ok) {
        if (result.code === 'RESUME_REQUIRED') {
          setMessage({ type: 'error', text: '학력 항목 정보를 먼저 입력하고 이력서를 저장해주세요.' });
          return;
        }
        throw new Error(result.error || '학위증명서 업로드에 실패했습니다.');
      }
      // 응답에 갱신된 education 배열 반영 — 단, 사용자가 입력 중인 다른 필드는 보존하기 위해 머지
      const serverEducation: any[] = result.data?.education || [];
      setEducation(prev => prev.map(item => {
        const updated = serverEducation.find((s: any) => s.id === item.id);
        return updated ? { ...item, ...updated } : item;
      }));
      setMessage({ type: 'success', text: '학위증명서가 업로드되었습니다. 관리자 검토 후 승인됩니다.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || '학위증명서 업로드에 실패했습니다.' });
    } finally {
      setUploadingDegreeId(null);
    }
  };

  const handleCredentialFileChange = (certId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: '이미지(JPG/PNG/WEBP/GIF/HEIC) 또는 PDF만 업로드할 수 있습니다.' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: '파일 크기는 10MB 이하여야 합니다.' });
      return;
    }

    const item = certificatesList.find(c => c.id === certId);
    if (item?.credentialUrl) {
      setCredentialReuploadConfirm({ certId, file });
    } else {
      doUploadCertificateCredential(certId, file);
    }
  };

  const doUploadCertificateCredential = async (certId: string, file: File) => {
    setUploadingCredentialId(certId);
    setCredentialReuploadConfirm(null);
    setMessage(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('certId', certId);
      const response = await fetch('/api/v1/resume/certificate-credential', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const result = await response.json();
      if (!response.ok) {
        if (result.code === 'RESUME_REQUIRED') {
          setMessage({ type: 'error', text: '자격증 정보를 먼저 입력하고 이력서를 저장해주세요.' });
          return;
        }
        throw new Error(result.error || '자격증 증명 파일 업로드에 실패했습니다.');
      }
      const serverList: any[] = result.data?.certificatesList || [];
      setCertificatesList(prev => prev.map(item => {
        const updated = serverList.find((s: any) => s.id === item.id);
        return updated ? { ...item, ...updated } : item;
      }));
      setMessage({ type: 'success', text: '자격증 증명 파일이 업로드되었습니다. 관리자 검토 후 인증됩니다.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || '자격증 증명 파일 업로드에 실패했습니다.' });
    } finally {
      setUploadingCredentialId(null);
    }
  };

  // --- List management helpers ---
  const addEducation = (type: string) => setEducation(prev => [...prev, emptyEducation(type)]);
  const removeEducation = (id: string) => setEducation(prev => prev.filter(e => e.id !== id));
  const updateEducation = (id: string, field: string, value: string) =>
    setEducation(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));

  const addExperience = (type: string) => setExperience(prev => [...prev, emptyExperience(type)]);
  const removeExperience = (id: string) => setExperience(prev => prev.filter(e => e.id !== id));
  const updateExperience = (id: string, field: string, value: string) =>
    setExperience(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));

  const addAward = () => setAwards(prev => [...prev, emptyAward()]);
  const removeAward = (id: string) => setAwards(prev => prev.filter(a => a.id !== id));
  const updateAward = (id: string, field: string, value: string) =>
    setAwards(prev => prev.map(a => a.id === id ? { ...a, [field]: value } : a));

  const addCertificate = () => setCertificatesList(prev => [...prev, emptyCertificate()]);
  const removeCertificate = (id: string) => setCertificatesList(prev => prev.filter(c => c.id !== id));
  const updateCertificate = (id: string, field: string, value: string) =>
    setCertificatesList(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));

  // Group education/experience by type
  const groupByType = <T extends { type: string }>(items: T[]) => {
    return items.reduce((acc, item) => {
      (acc[item.type] = acc[item.type] || []).push(item);
      return acc;
    }, {} as Record<string, T[]>);
  };

  const groupedEducation = groupByType(education);
  const groupedExperience = groupByType(experience);

  // --- Section header with visibility toggle ---
  const SectionHeader = ({ title, icon: Icon, visKey }: { title: string; icon: any; visKey: keyof Visibility }) => (
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <Icon className="w-5 h-5 text-primary" />
        {title}
      </h3>
      <button
        onClick={() => toggleVisibility(visKey)}
        className={`flex items-center gap-1.5 text-sm px-3 py-1 rounded-full transition-colors ${
          visibility[visKey] ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
        }`}
        title={visibility[visKey] ? '공개 중 (클릭하여 비공개)' : '비공개 (클릭하여 공개)'}
      >
        {visibility[visKey] ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
        {visibility[visKey] ? '공개' : '비공개'}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <MainTabsNav className="mt-16" />
        <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
          <AdminSidebar />
          <main className="flex-1 p-4 flex items-center justify-center text-gray-500">로딩 중...</main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />
      <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
        <AdminSidebar />

        <main className="flex-1 min-w-0 p-4 sm:px-10 lg:px-16">
          {/* 헤더 */}
          <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6 text-primary" />
                이력서
              </h1>
              <p className="text-gray-500 mt-1">이력서를 작성하고 구직 상태를 관리하세요.</p>
            </div>
            <div className="flex items-center gap-3">
              {/* 구직 ON/OFF 토글 */}
              <button
                onClick={handleToggleJobSeeking}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${
                  isJobSeeking
                    ? 'bg-green-500 text-white shadow-lg shadow-green-200'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {isJobSeeking ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                구직 {isJobSeeking ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>

          {/* 메시지 */}
          {message && (
            <div className={`mb-4 p-3 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {message.text}
            </div>
          )}

          <div className="space-y-6">
            {/* 프로필 사진 + 기본 정보 */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <SectionHeader title="기본 정보" icon={User} visKey="introduction" />
              <div className="flex gap-6">
                {/* 프로필 사진 */}
                <div className="flex-shrink-0 relative w-32">
                  <label className="cursor-pointer group block">
                    <div className="w-32 h-32 rounded-xl border-2 border-dashed border-gray-300 group-hover:border-primary overflow-hidden flex items-center justify-center bg-gray-50 transition-colors">
                      {photoUrl ? (
                        <img src={photoUrl} alt="프로필" className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center">
                          <Camera className="w-8 h-8 text-gray-400 mx-auto mb-1" />
                          <span className="text-xs text-gray-400">사진 업로드</span>
                        </div>
                      )}
                    </div>
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                  </label>
                  {photoUrl && (
                    <button
                      type="button"
                      onClick={handleDeletePhoto}
                      disabled={isDeletingPhoto}
                      className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white border border-gray-300 shadow-sm flex items-center justify-center text-gray-500 hover:text-red-500 hover:border-red-300 transition-colors disabled:opacity-50"
                      title="사진 삭제"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="flex-1 space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1">자기소개</label>
                    <RichTextEditor
                      content={introduction}
                      onChange={setIntroduction}
                      placeholder="자기소개를 작성해주세요."
                      minHeight="150px"
                      disableImage
                    />
                  </div>
                </div>
              </div>

              {/* 포트폴리오 이미지 (최대 3개) */}
              <div className="mt-6 pt-5 border-t border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-primary" />
                    포트폴리오 이미지
                    <span className="text-xs font-normal text-gray-500">
                      ({portfolioImages.filter(u => u).length}/3)
                    </span>
                  </h4>
                  <button
                    onClick={() => toggleVisibility('portfolio')}
                    className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full transition-colors ${
                      visibility.portfolio ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {visibility.portfolio ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    {visibility.portfolio ? '공개' : '비공개'}
                  </button>
                </div>
                <div className="flex flex-wrap gap-3 items-start">
                  {[0, 1, 2].map(slot => {
                    const url = getPortfolioDisplayUrl(slot);
                    const dirty = isPortfolioSlotDirty(slot);
                    const pending = portfolioPending[slot] || {};
                    const hasImage = !!url;
                    return (
                      <div
                        key={slot}
                        className={`relative group h-48 ${hasImage ? '' : 'w-48'} rounded-lg border-2 ${dirty ? 'border-amber-400 border-solid' : 'border-dashed border-gray-300'} overflow-hidden bg-gray-50`}
                      >
                        {hasImage ? (
                          <>
                            <img src={url} alt={`포트폴리오 ${slot + 1}`} className="block h-full w-auto" />
                            <button
                              type="button"
                              onClick={() => handlePortfolioRemove(slot)}
                              className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              title="삭제"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                            <label className="absolute bottom-2 right-2 bg-white/90 text-gray-700 px-2 py-1 rounded text-xs cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white">
                              교체
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handlePortfolioPick(slot, e)}
                              />
                            </label>
                            {pending.file && (
                              <span className="absolute top-2 left-2 bg-amber-500 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full">저장 시 반영</span>
                            )}
                          </>
                        ) : (
                          <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 hover:border-primary transition-colors">
                            <Plus className="w-6 h-6 text-gray-400 mb-1" />
                            <span className="text-xs text-gray-500">이미지 추가</span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handlePortfolioPick(slot, e)}
                            />
                          </label>
                        )}
                        {pending.delete && (
                          <div className="absolute inset-0 bg-red-50/90 flex flex-col items-center justify-center gap-1.5">
                            <span className="text-xs font-medium text-red-700">삭제 예약</span>
                            <button
                              type="button"
                              onClick={() => handlePortfolioRevert(slot)}
                              className="text-xs px-2 py-0.5 bg-white border border-red-300 text-red-700 rounded hover:bg-red-100"
                            >
                              되돌리기
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  최대 3장, 5MB 이하 이미지 파일
                  {hasPortfolioChanges && (
                    <span className="ml-2 text-amber-600 font-medium">· 저장하지 않은 변경 있음</span>
                  )}
                </p>
                <div className="mt-2.5 bg-blue-50 border border-blue-100 rounded-lg px-3.5 py-2.5 text-xs text-blue-700 leading-relaxed">
                  <span className="font-semibold">스타일 3 (가로형) 권장 이미지 비율</span>
                  <span className="mx-1.5 text-blue-300">·</span>
                  1장 <span className="font-medium">7:8</span>
                  <span className="mx-1.5 text-blue-300">·</span>
                  2장 <span className="font-medium">9:16</span>
                  <span className="mx-1.5 text-blue-300">·</span>
                  3장 <span className="font-medium">3:5</span>
                  <span className="ml-1.5 text-blue-500">(모두 세로형)</span>
                </div>
              </div>
            </div>

            {/* 희망 구직 조건 */}
            {isJobSeeking && (
              <div className="bg-green-50 rounded-xl border border-green-200 p-6">
                <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  희망 구직 조건
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1">희망 포지션</label>
                    <input
                      type="text"
                      value={desiredPosition}
                      onChange={e => setDesiredPosition(e.target.value)}
                      placeholder="예: 필라테스 강사, PT 트레이너"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1">희망 고용 형태</label>
                    <select
                      value={desiredWorkType}
                      onChange={e => setDesiredWorkType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary"
                    >
                      {DESIRED_WORK_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1">희망 급여 형태</label>
                    <select
                      value={desiredSalaryType}
                      onChange={e => setDesiredSalaryType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary"
                    >
                      {SALARY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  {desiredSalaryType !== 'NEGOTIABLE' && (
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="block text-sm font-semibold text-gray-900 mb-1">최소 (원)</label>
                        <input
                          type="number" value={desiredSalaryMin}
                          onChange={e => setDesiredSalaryMin(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-semibold text-gray-900 mb-1">최대 (원)</label>
                        <input
                          type="number" value={desiredSalaryMax}
                          onChange={e => setDesiredSalaryMax(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1">희망 근무 지역</label>
                    <div className="flex gap-2">
                      <select
                        value={desiredLocationProvince}
                        onChange={e => { setDesiredLocationProvince(e.target.value); setDesiredLocationDistrict(''); }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary bg-white"
                      >
                        <option value="">시/도 선택</option>
                        {getAllProvinces().map(province => (
                          <option key={province} value={province}>{province}</option>
                        ))}
                      </select>
                      <select
                        value={desiredLocationDistrict}
                        onChange={e => setDesiredLocationDistrict(e.target.value)}
                        disabled={!desiredLocationProvince}
                        className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary bg-white ${
                          !desiredLocationProvince ? 'border-gray-200 bg-gray-50 cursor-not-allowed' : 'border-gray-300'
                        }`}
                      >
                        <option value="">시/군/구 선택</option>
                        {desiredLocationProvince && getDistricts(desiredLocationProvince).map(district => (
                          <option key={district} value={district}>{district}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <AddressSearch value={address} onChange={setAddress} />
                </div>
              </div>
            )}

            {/* 학력 */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <SectionHeader title="학력" icon={GraduationCap} visKey="education" />
              <p className="text-xs text-gray-500 mb-4 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                관련 증명서 제출이 완료된 경우에 한해 인증이 적용되며, 미제출 또는 요건 미충족 시에는 '미인증' 상태로 표시됩니다.
              </p>
              {EDUCATION_TYPES.map(type => {
                const items = groupedEducation[type.value] || [];
                return (
                  <div key={type.value} className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-700">{type.label}</h4>
                      <button
                        onClick={() => addEducation(type.value)}
                        className="text-xs text-primary hover:text-primary-600 flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> 추가
                      </button>
                    </div>
                    {items.map(item => {
                      const review = item.degreeReviewStatus;
                      const isApproved = review === 'APPROVED';
                      const reviewBadge = !item.degreeCertificateUrl
                        ? null
                        : review === 'APPROVED'
                          ? { icon: CheckCircle, color: 'text-green-600 bg-green-50 border-green-200', text: '승인됨' }
                          : review === 'REJECTED'
                            ? { icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200', text: '반려됨' }
                            : { icon: Clock, color: 'text-amber-600 bg-amber-50 border-amber-200', text: '검토 대기' };

                      return (
                        <div key={item.id} className={`rounded-lg p-3 mb-3 border ${isApproved ? 'bg-green-50/40 border-green-200' : 'bg-gray-50/50 border-gray-100'}`}>
                          {isApproved && (
                            <div className="flex items-center gap-1.5 mb-2 text-xs text-green-700 font-medium">
                              <Lock className="w-3 h-3" />
                              학위 인증 완료 — 인증된 항목은 수정하거나 삭제할 수 없습니다.
                            </div>
                          )}
                          <div className="flex items-start gap-2 mb-2">
                            <input
                              type="text" placeholder="연도" value={item.year}
                              onChange={e => updateEducation(item.id, 'year', e.target.value)}
                              disabled={isApproved}
                              className={`w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm ${isApproved ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
                            />
                            <input
                              type="text" placeholder="학교/기관명" value={item.schoolName}
                              onChange={e => updateEducation(item.id, 'schoolName', e.target.value)}
                              disabled={isApproved}
                              className={`${item.type === 'GRADUATE' ? 'flex-[0.85] min-w-0' : 'flex-1'} px-2 py-1.5 border border-gray-300 rounded-lg text-sm ${isApproved ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
                            />
                            <input
                              type="text" placeholder="전공/과정" value={item.major}
                              onChange={e => updateEducation(item.id, 'major', e.target.value)}
                              disabled={isApproved}
                              className={`${item.type === 'GRADUATE' ? 'flex-[0.85] min-w-0' : 'flex-1'} px-2 py-1.5 border border-gray-300 rounded-lg text-sm ${isApproved ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
                            />
                            {item.type === 'GRADUATE' && (
                              <select
                                value={item.degreeLevel || 'MASTERS'}
                                onChange={e => updateEducation(item.id, 'degreeLevel', e.target.value)}
                                disabled={isApproved}
                                className={`w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm ${isApproved ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
                              >
                                {DEGREE_LEVELS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                              </select>
                            )}
                            <select
                              value={item.status}
                              onChange={e => updateEducation(item.id, 'status', e.target.value)}
                              disabled={isApproved}
                              className={`w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm ${isApproved ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
                            >
                              {EDUCATION_STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                            {isApproved ? (
                              <div className="p-1.5 text-green-400" title="인증 완료된 항목은 삭제할 수 없습니다.">
                                <Lock className="w-4 h-4" />
                              </div>
                            ) : (
                              <button
                                onClick={() => removeEducation(item.id)}
                                className="p-1.5 text-gray-400 hover:text-red-500"
                                title="이 학력 삭제"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          {/* 학위증명서 업로드 */}
                          <div className="flex items-center gap-2 ml-1 mt-1.5">
                            <Paperclip className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <span className="text-xs text-gray-600 shrink-0">학위증명서</span>
                            {item.degreeCertificateUrl ? (
                              <a
                                href={item.degreeCertificateUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline flex items-center gap-0.5"
                              >
                                업로드된 파일 보기
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : (
                              <span className="text-xs text-gray-400">미인증</span>
                            )}
                            {reviewBadge && (
                              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${reviewBadge.color}`}>
                                <reviewBadge.icon className="w-3 h-3" />
                                {reviewBadge.text}
                              </span>
                            )}
                            {!isApproved && (
                              <label className="ml-auto text-xs text-primary hover:text-primary-600 cursor-pointer flex items-center gap-1">
                                <Upload className="w-3 h-3" />
                                {uploadingDegreeId === item.id
                                  ? '업로드 중...'
                                  : item.degreeCertificateUrl ? '재업로드' : '파일 첨부'}
                                <input
                                  type="file"
                                  accept="image/*,application/pdf"
                                  className="hidden"
                                  disabled={uploadingDegreeId === item.id}
                                  onChange={e => handleDegreeFileChange(item.id, e)}
                                />
                              </label>
                            )}
                          </div>
                          {review === 'REJECTED' && item.degreeRejectionReason && (
                            <p className="text-xs text-red-600 ml-6 mt-1">
                              반려 사유: {item.degreeRejectionReason}
                            </p>
                          )}
                        </div>
                      );
                    })}
                    {items.length === 0 && (
                      <p className="text-xs text-gray-400 ml-1">항목이 없습니다. 추가 버튼을 눌러주세요.</p>
                    )}
                  </div>
                );
              })}
              <p className="text-xs text-gray-500 mt-2 flex items-start gap-1">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                <span>학위증명서는 이미지(JPG/PNG/WEBP/GIF/HEIC) 또는 PDF, 최대 10MB. 업로드 후 관리자 검토 승인이 필요합니다.</span>
              </p>
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                <p className="text-xs text-red-700 leading-relaxed">
                  <span className="font-semibold">⚠ 위·변조 및 도용 금지</span><br />
                  반드시 본인 명의의 학위증명서만 제출해주세요. 타인의 증명서를 도용하거나 위·변조된 자료를 업로드하는 행위는 <strong>사문서위조·동행사죄</strong>에 해당하며, 적발 시 형사 고발 및 서비스 이용이 영구 제한될 수 있습니다.
                </p>
              </div>
            </div>

            {/* 경력 */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <SectionHeader title="경력" icon={Briefcase} visKey="experience" />
              {EXPERIENCE_TYPES.map(type => {
                const items = groupedExperience[type.value] || [];
                return (
                  <div key={type.value} className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-700">{type.label}</h4>
                      <button
                        onClick={() => addExperience(type.value)}
                        className="text-xs text-primary hover:text-primary-600 flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> 추가
                      </button>
                    </div>
                    {items.map(item => (
                      <div key={item.id} className="flex items-start gap-2 mb-2">
                        <input
                          type="text" placeholder="연도" value={item.year}
                          onChange={e => updateExperience(item.id, 'year', e.target.value)}
                          className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                        />
                        <input
                          type="text" placeholder="회사/기관명" value={item.company}
                          onChange={e => updateExperience(item.id, 'company', e.target.value)}
                          className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                        />
                        <input
                          type="text" placeholder="직책/역할" value={item.position}
                          onChange={e => updateExperience(item.id, 'position', e.target.value)}
                          className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                        />
                        <button
                          onClick={() => removeExperience(item.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {items.length === 0 && (
                      <p className="text-xs text-gray-400 ml-1">항목이 없습니다.</p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* 수상 */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <SectionHeader title="수상 내역 및 위촉 사항" icon={Award} visKey="awards" />
              {awards.map(item => (
                <div key={item.id} className="flex items-start gap-2 mb-2">
                  <input
                    type="text" placeholder="연도" value={item.year}
                    onChange={e => updateAward(item.id, 'year', e.target.value)}
                    className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                  <input
                    type="text" placeholder="수상명" value={item.title}
                    onChange={e => updateAward(item.id, 'title', e.target.value)}
                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                  <button onClick={() => removeAward(item.id)} className="p-1.5 text-gray-400 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button onClick={addAward} className="text-xs text-primary hover:text-primary-600 flex items-center gap-1 mt-2">
                <Plus className="w-3 h-3" /> 추가
              </button>
            </div>

            {/* 보유 자격증 및 수료 내역 (수동 입력) */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <SectionHeader title="보유 자격증 및 수료 내역" icon={ScrollText} visKey="certificates_list" />
              <p className="text-xs text-gray-500 mb-4 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                관련 증명서 제출이 완료된 경우에 한해 인증이 적용되며, 미제출 또는 요건 미충족 시에는 '미인증' 상태로 표시됩니다.
              </p>
              {certificatesList.map(item => {
                const review = item.reviewStatus;
                const isApproved = review === 'APPROVED';
                const reviewBadge = !item.credentialUrl
                  ? null
                  : review === 'APPROVED'
                    ? { icon: CheckCircle, color: 'text-green-600 bg-green-50 border-green-200', text: '인증됨' }
                    : review === 'REJECTED'
                      ? { icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200', text: '반려됨' }
                      : { icon: Clock, color: 'text-amber-600 bg-amber-50 border-amber-200', text: '검토 대기' };

                return (
                  <div key={item.id} className={`rounded-lg p-3 mb-3 border ${isApproved ? 'bg-green-50/40 border-green-200' : 'bg-gray-50/50 border-gray-100'}`}>
                    {isApproved && (
                      <div className="flex items-center gap-1.5 mb-2 text-xs text-green-700 font-medium">
                        <Lock className="w-3 h-3" />
                        인증 완료 — 인증된 항목은 수정하거나 삭제할 수 없습니다.
                      </div>
                    )}
                    <div className="flex items-start gap-2 mb-2">
                      <input
                        type="text" placeholder="자격증명" value={item.name}
                        onChange={e => updateCertificate(item.id, 'name', e.target.value)}
                        disabled={isApproved}
                        className={`flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-sm ${isApproved ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
                      />
                      <input
                        type="text" placeholder="발급 기관" value={item.issuer}
                        onChange={e => updateCertificate(item.id, 'issuer', e.target.value)}
                        disabled={isApproved}
                        className={`flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-sm ${isApproved ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
                      />
                      <input
                        type="text" placeholder="취득일" value={item.date}
                        onChange={e => updateCertificate(item.id, 'date', e.target.value)}
                        disabled={isApproved}
                        className={`w-28 px-2 py-1.5 border border-gray-300 rounded-lg text-sm ${isApproved ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
                      />
                      {isApproved ? (
                        <div className="p-1.5 text-green-400" title="인증 완료된 항목은 삭제할 수 없습니다.">
                          <Lock className="w-4 h-4" />
                        </div>
                      ) : (
                        <button onClick={() => removeCertificate(item.id)} className="p-1.5 text-gray-400 hover:text-red-500" title="이 자격증 삭제">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* 증명 파일 업로드 */}
                    <div className="flex items-center gap-2 ml-1 mt-1.5">
                      <Paperclip className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span className="text-xs text-gray-600 shrink-0">자격증/수료증</span>
                      {item.credentialUrl ? (
                        <a
                          href={item.credentialUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline flex items-center gap-0.5"
                        >
                          업로드된 파일 보기
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">미인증</span>
                      )}
                      {reviewBadge && (
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${reviewBadge.color}`}>
                          <reviewBadge.icon className="w-3 h-3" />
                          {reviewBadge.text}
                        </span>
                      )}
                      {!isApproved && (
                        <label className="ml-auto text-xs text-primary hover:text-primary-600 cursor-pointer flex items-center gap-1">
                          <Upload className="w-3 h-3" />
                          {uploadingCredentialId === item.id
                            ? '업로드 중...'
                            : item.credentialUrl ? '재업로드' : '파일 첨부'}
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            className="hidden"
                            disabled={uploadingCredentialId === item.id}
                            onChange={e => handleCredentialFileChange(item.id, e)}
                          />
                        </label>
                      )}
                    </div>
                    {review === 'REJECTED' && item.rejectionReason && (
                      <p className="text-xs text-red-600 ml-6 mt-1">
                        반려 사유: {item.rejectionReason}
                      </p>
                    )}
                  </div>
                );
              })}
              <button onClick={addCertificate} className="text-xs text-primary hover:text-primary-600 flex items-center gap-1 mt-2">
                <Plus className="w-3 h-3" /> 추가
              </button>
            </div>

            {/* StampFit 발급 인증서 (자동 연동) */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Award className="w-5 h-5 text-accent" />
                  StampFit 발급 인증서
                </h3>
                <button
                  onClick={() => toggleVisibility('stampfit_certificates')}
                  className={`flex items-center gap-1.5 text-sm px-3 py-1 rounded-full transition-colors ${
                    visibility.stampfit_certificates ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {visibility.stampfit_certificates ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  {visibility.stampfit_certificates ? '공개' : '비공개'}
                </button>
              </div>
              {stampfitCerts.length > 0 ? (
                <div className="space-y-2">
                  {stampfitCerts.map((cert: any) => {
                    const hours = [
                      cert.class?.offline_hours > 0 ? `오프라인 ${cert.class.offline_hours}h` : null,
                      cert.class?.online_hours > 0 ? `온라인 ${cert.class.online_hours}h` : null,
                    ].filter(Boolean).join(' / ');
                    return (
                    <div key={cert.id} className="flex items-start justify-between p-3 bg-accent/5 rounded-lg border border-accent/20 gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900">{cert.class_name}</p>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {cert.provider?.company_name && (
                            <span className="mr-2">{cert.provider.company_name}</span>
                          )}
                          {hours && <span className="mr-2 text-gray-400">·</span>}
                          {hours && <span className="mr-2">{hours}</span>}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          발급일: {new Date(cert.issued_at).toLocaleDateString('ko-KR')}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs bg-accent/10 text-accent px-2 py-1 rounded-full font-medium">
                        StampFit 인증
                      </span>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-400">StampFit에서 발급받은 인증서가 없습니다.</p>
              )}
            </div>

            {/* 버튼 영역 */}
            <div className="flex justify-end gap-3 pb-8">
              <button
                onClick={() => setShowPreview(true)}
                className="flex items-center gap-2 px-6 py-3 border border-primary text-primary rounded-lg font-semibold hover:bg-primary/5 transition-colors"
              >
                <FileSearch className="w-5 h-5" />
                미리보기
              </button>
              <button
                onClick={() => setShowSaveModal(true)}
                disabled={saving}
                className="relative flex items-center gap-2 px-8 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 shadow-lg"
              >
                <Save className="w-5 h-5" />
                {saving ? '저장 중...' : '이력서 저장'}
                {hasPortfolioChanges && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full ring-2 ring-white" title="저장되지 않은 변경 있음" />
                )}
              </button>
            </div>
          </div>

          {/* 학위증명서 재업로드 경고 모달 */}
          {reuploadConfirm && (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setReuploadConfirm(null)}>
              <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-amber-500" />
                  <h3 className="text-lg font-semibold text-gray-900">학위증명서를 다시 업로드하시겠습니까?</h3>
                </div>
                <div className="space-y-2 text-sm text-gray-600 mb-6">
                  <p>이미 업로드된 학위증명서가 있습니다.</p>
                  <p className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
                    재업로드하면 <strong>승인 상태가 초기화</strong>되고, 관리자가 다시 검토해야 합니다.
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setReuploadConfirm(null)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                  >
                    취소
                  </button>
                  <button
                    onClick={() => doUploadDegreeCertificate(reuploadConfirm.eduId, reuploadConfirm.file)}
                    className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
                  >
                    재업로드 진행
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 자격증 증명 파일 재업로드 경고 모달 */}
          {credentialReuploadConfirm && (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setCredentialReuploadConfirm(null)}>
              <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-amber-500" />
                  <h3 className="text-lg font-semibold text-gray-900">자격증 증명 파일을 다시 업로드하시겠습니까?</h3>
                </div>
                <div className="space-y-2 text-sm text-gray-600 mb-6">
                  <p>이미 업로드된 증명 파일이 있습니다.</p>
                  <p className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
                    재업로드하면 <strong>인증 상태가 초기화</strong>되고, 관리자가 다시 검토해야 합니다.
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setCredentialReuploadConfirm(null)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                  >
                    취소
                  </button>
                  <button
                    onClick={() => doUploadCertificateCredential(credentialReuploadConfirm.certId, credentialReuploadConfirm.file)}
                    className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
                  >
                    재업로드 진행
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 이력서 미리보기 모달 — 템플릿 1 */}
          {showPreview && (
            <>
              {/* Screen modal */}
              <div
                className="fixed inset-0 z-50 bg-black/65 flex items-center justify-center p-4"
                onClick={() => setShowPreview(false)}
              >
                <div
                  className="rounded-2xl shadow-2xl bg-gray-100 flex flex-col"
                  style={{ width: previewStyle === 3 ? 1210 : 858, maxWidth: 'calc(100vw - 32px)', height: 'calc(100vh - 32px)', minWidth: 0 }}
                  onClick={e => e.stopPropagation()}
                >
                  {/* 모달 헤더 — 고정 */}
                  <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between rounded-t-2xl">
                    <div className="flex items-center gap-3">
                      <h2 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                        <FileSearch className="w-4 h-4 text-primary" />
                        이력서 미리보기
                      </h2>
                      <div className="flex items-center gap-1">
                        {([1, 2] as const).map(s => (
                          <button
                            key={s}
                            onClick={() => setPreviewStyle(s)}
                            className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition-colors ${
                              previewStyle === s
                                ? 'bg-primary text-white'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                          >
                            스타일 {s}
                          </button>
                        ))}
                        <button
                          onClick={() => setPreviewStyle(3)}
                          className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition-colors ${
                            previewStyle === 3
                              ? 'bg-primary text-white'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}
                        >
                          스타일 3
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowPreview(false)}
                      className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                    >
                      &times;
                    </button>
                  </div>

                  {/* Template pages — 스케일러가 모달 높이에 맞게 축소 */}
                  <div className="flex-1 min-h-0 overflow-hidden relative">
                    <ResumeModalScaler
                      pageW={previewStyle === 3 ? 1123 : 794}
                      pageH={previewStyle === 3 ? 794 : 1123}
                    >
                      {previewStyle === 1 && (
                        <ResumeTemplate1Preview
                          user={user}
                          photoUrl={photoUrl}
                          education={education}
                          experience={experience}
                          awards={awards}
                          certificatesList={certificatesList}
                          stampfitCerts={stampfitCerts}
                          introduction={introduction}
                          visibility={visibility}
                          desiredPosition={desiredPosition}
                          address={address}
                          getPortfolioDisplayUrl={getPortfolioDisplayUrl}
                        />
                      )}
                      {previewStyle === 2 && (
                        <ResumeTemplate2Preview
                          user={user}
                          photoUrl={photoUrl}
                          education={education}
                          experience={experience}
                          awards={awards}
                          certificatesList={certificatesList}
                          stampfitCerts={stampfitCerts}
                          introduction={introduction}
                          visibility={visibility}
                          desiredPosition={desiredPosition}
                          address={address}
                          getPortfolioDisplayUrl={getPortfolioDisplayUrl}
                        />
                      )}
                      {previewStyle === 3 && (
                        <ResumeTemplate3Preview
                          user={user}
                          photoUrl={photoUrl}
                          education={education}
                          experience={experience}
                          awards={awards}
                          certificatesList={certificatesList}
                          stampfitCerts={stampfitCerts}
                          introduction={introduction}
                          visibility={visibility}
                          desiredPosition={desiredPosition}
                          address={address}
                          getPortfolioDisplayUrl={getPortfolioDisplayUrl}
                        />
                      )}
                    </ResumeModalScaler>
                  </div>

                  {/* 모달 푸터 — 고정 */}
                  <div className="flex-shrink-0 bg-gray-50 border-t border-gray-200 px-4 py-2 flex justify-end gap-2 rounded-b-2xl">
                    <button
                      onClick={() => setShowPreview(false)}
                      className="px-4 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-xs font-medium"
                    >
                      닫기
                    </button>
                    <button
                      onClick={handlePrint}
                      className="flex items-center gap-1.5 px-4 py-1.5 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary-600"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      PDF 출력
                    </button>
                  </div>
                </div>
              </div>

              {/* Hidden print root — rendered off-screen for window.print() */}
              <div
                id="resume-print-root"
                style={{ position: 'fixed', left: '-9999px', top: 0, zIndex: -1 }}
                aria-hidden="true"
              >
                {previewStyle === 1 && (
                  <ResumeTemplate1Preview
                    user={user}
                    photoUrl={photoUrl}
                    education={education}
                    experience={experience}
                    awards={awards}
                    certificatesList={certificatesList}
                    stampfitCerts={stampfitCerts}
                    introduction={introduction}
                    visibility={visibility}
                    desiredPosition={desiredPosition}
                    address={address}
                    getPortfolioDisplayUrl={getPortfolioDisplayUrl}
                  />
                )}
                {previewStyle === 2 && (
                  <ResumeTemplate2Preview
                    user={user}
                    photoUrl={photoUrl}
                    education={education}
                    experience={experience}
                    awards={awards}
                    certificatesList={certificatesList}
                    stampfitCerts={stampfitCerts}
                    introduction={introduction}
                    visibility={visibility}
                    desiredPosition={desiredPosition}
                    address={address}
                    getPortfolioDisplayUrl={getPortfolioDisplayUrl}
                  />
                )}
                {previewStyle === 3 && (
                  <ResumeTemplate3Preview
                    user={user}
                    photoUrl={photoUrl}
                    education={education}
                    experience={experience}
                    awards={awards}
                    certificatesList={certificatesList}
                    stampfitCerts={stampfitCerts}
                    introduction={introduction}
                    visibility={visibility}
                    desiredPosition={desiredPosition}
                    address={address}
                    getPortfolioDisplayUrl={getPortfolioDisplayUrl}
                  />
                )}
              </div>
            </>
          )}

          {/* ── 이력서 저장 모달 (템플릿 선택 → 확인) ── */}
          {showSaveModal && (
            <>
              <div
                className="fixed inset-0 z-50 bg-black/65 flex items-center justify-center p-4"
                onClick={() => setShowSaveModal(false)}
              >
                <div
                  className="rounded-2xl shadow-2xl bg-gray-100 flex flex-col"
                  style={{ width: previewStyle === 3 ? 1210 : 858, maxWidth: 'calc(100vw - 32px)', height: 'calc(100vh - 32px)', minWidth: 0 }}
                  onClick={e => e.stopPropagation()}
                >
                  {/* 헤더 */}
                  <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between rounded-t-2xl">
                    <div className="flex items-center gap-3">
                      <h2 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                        <Save className="w-4 h-4 text-primary" />
                        이력서 저장
                      </h2>
                      <div className="flex items-center gap-1">
                        {([1, 2] as const).map(s => (
                          <button
                            key={s}
                            onClick={() => setPreviewStyle(s)}
                            className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition-colors ${previewStyle === s ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                          >
                            스타일 {s}
                          </button>
                        ))}
                        <button
                          onClick={() => setPreviewStyle(3)}
                          className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition-colors ${previewStyle === 3 ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                        >
                          스타일 3
                        </button>
                      </div>
                      <span className="text-xs text-gray-400">선택한 스타일로 저장됩니다</span>
                    </div>
                    <button onClick={() => setShowSaveModal(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
                  </div>

                  {/* 템플릿 미리보기 */}
                  <div className="flex-1 min-h-0 overflow-hidden relative">
                    <ResumeModalScaler
                      pageW={previewStyle === 3 ? 1123 : 794}
                      pageH={previewStyle === 3 ? 794 : 1123}
                    >
                      {previewStyle === 1 && (
                        <ResumeTemplate1Preview
                          user={user}
                          photoUrl={photoUrl}
                          education={education}
                          experience={experience}
                          awards={awards}
                          certificatesList={certificatesList}
                          stampfitCerts={stampfitCerts}
                          introduction={introduction}
                          visibility={visibility}
                          desiredPosition={desiredPosition}
                          address={address}
                          getPortfolioDisplayUrl={getPortfolioDisplayUrl}
                        />
                      )}
                      {previewStyle === 2 && (
                        <ResumeTemplate2Preview
                          user={user}
                          photoUrl={photoUrl}
                          education={education}
                          experience={experience}
                          awards={awards}
                          certificatesList={certificatesList}
                          stampfitCerts={stampfitCerts}
                          introduction={introduction}
                          visibility={visibility}
                          desiredPosition={desiredPosition}
                          address={address}
                          getPortfolioDisplayUrl={getPortfolioDisplayUrl}
                        />
                      )}
                      {previewStyle === 3 && (
                        <ResumeTemplate3Preview
                          user={user}
                          photoUrl={photoUrl}
                          education={education}
                          experience={experience}
                          awards={awards}
                          certificatesList={certificatesList}
                          stampfitCerts={stampfitCerts}
                          introduction={introduction}
                          visibility={visibility}
                          desiredPosition={desiredPosition}
                          address={address}
                          getPortfolioDisplayUrl={getPortfolioDisplayUrl}
                        />
                      )}
                    </ResumeModalScaler>
                  </div>

                  {/* 푸터 */}
                  <div className="flex-shrink-0 bg-gray-50 border-t border-gray-200 px-4 py-2 flex justify-end gap-2 rounded-b-2xl">
                    <button
                      onClick={() => setShowSaveModal(false)}
                      className="px-4 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-xs font-medium"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-1.5 px-4 py-1.5 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary-600 disabled:opacity-50"
                    >
                      <Save className="w-3.5 h-3.5" />
                      {saving ? '저장 중...' : '확인'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Hidden print root는 preview 모달과 공유 */}
            </>
          )}

          {/* 프로필 사진 크롭 모달 */}
          {photoImageToCrop && (
            <ProfilePhotoCropModal
              imageUrl={photoImageToCrop}
              onClose={handleClosePhotoCropModal}
              onSave={handleSaveCroppedPhoto}
              isSaving={isSavingPhoto}
            />
          )}
        </main>
      </div>
    </div>
  );
}
